import moment from 'moment';
import { useMember } from '../context/UserContext';
import { ChapterPlan } from '../types/membership';

/** Ordered plan ranks: free < plus < managed. Mirrors api.has_plan in the DB. */
export const PLAN_RANK: Record<ChapterPlan, number> = {
  free: 0,
  plus: 1,
  managed: 2,
};

/**
 * usePlan hook
 * Reads the chapter's plan from the member context (no extra fetch).
 * `effectivePlan` degrades to 'free' once plan_expires_at passes (a null
 * expiry never expires) — mirrors api.effective_plan in the DB.
 */
export function usePlan() {
  const { member, loading } = useMember();

  const plan: ChapterPlan = member?.chapters?.plan ?? 'free';
  const planExpiresAt = member?.chapters?.plan_expires_at ?? null;

  const expired = planExpiresAt !== null && moment(planExpiresAt).isSameOrBefore(moment());
  const effectivePlan: ChapterPlan = expired ? 'free' : plan;

  return {
    plan,
    effectivePlan,
    planExpiresAt,
    isPlus: PLAN_RANK[effectivePlan] >= PLAN_RANK.plus,
    loading,
  };
}

/**
 * Checks whether the chapter's effective plan meets the required tier
 * (ordered check — a managed chapter satisfies a 'plus' requirement).
 * Returns false while member data is loading.
 */
export function useHasPlan(required: ChapterPlan): boolean {
  const { effectivePlan, loading } = usePlan();
  return !loading && PLAN_RANK[effectivePlan] >= PLAN_RANK[required];
}
